import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { roleService } from "@/services/userService";
import type { Role } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

export function RoleManagementPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  const [editTarget, setEditTarget] = useState<Role | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const loadRoles = useCallback(async () => {
    try {
      const data = await roleService.list();
      setRoles(data);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleCreate = async () => {
    try {
      await roleService.create(newName, newDescription || undefined);
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      await loadRoles();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await roleService.remove(deleteTarget.id);
      setDeleteTarget(null);
      await loadRoles();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleOpenEdit = (role: Role) => {
    setEditTarget(role);
    setEditName(role.name);
    setEditDescription(role.description ?? "");
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    try {
      await roleService.update(editTarget.id, { name: editName, description: editDescription });
      setEditTarget(null);
      await loadRoles();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight={700}>
          Role Management
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
          New Role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>{role.name}</TableCell>
                <TableCell>{role.description ?? "—"}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenEdit(role)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => setDeleteTarget(role)}
                    disabled={role.name === "ADMIN" || role.name === "USER"}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create new role</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Role name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newName}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onClose={() => setEditTarget(null)}>
        <DialogTitle>Edit role {editTarget?.name}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Role name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editTarget?.name === "ADMIN" || editTarget?.name === "USER"}
            helperText={
              editTarget?.name === "ADMIN" || editTarget?.name === "USER"
                ? "Built-in roles cannot be renamed"
                : undefined
            }
            autoFocus
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={!editName}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete role {deleteTarget?.name}?</DialogTitle>
        <DialogContent>This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RoleManagementPage;
